import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  DevicesByWorkflowType,
  IntegrationDataType,
  VideoDeviceType,
} from '../../types/videoDeviceTypes';

export interface AltaVideoState {
  videoDevices: VideoDeviceType[];
  devicesByWorkflow: Record<number, DevicesByWorkflowType>;
  altaVideoIntegrationData: IntegrationDataType[];
  isAltaVideoInstalled: boolean;
  isLoggedIn: boolean;
  thumbnailPresence: Record<
    string,
    Record<string, { SIGNIN?: boolean; SIGNOUT?: boolean }>
  >;
  thumbnailLoading: Record<
    string,
    Record<string, { SIGNIN?: number; SIGNOUT?: number }>
  >;
}

const initialState: AltaVideoState = {
  videoDevices: [],
  devicesByWorkflow: {},
  altaVideoIntegrationData: [],
  isAltaVideoInstalled: false,
  isLoggedIn: false,
  thumbnailPresence: {},
  thumbnailLoading: {},
};

const altaVideoSlice = createSlice({
  name: 'altaVideo',
  initialState,
  reducers: {
    fetchAltaVideoIntegrationData(_state, _action: PayloadAction<number>) {},
    fetchDevicesByWorkflow(
      _state,
      _action: PayloadAction<{ orgId: number; locationId: number }>,
    ) {},
    fetchVideoDevices(_state, _action: PayloadAction<number>) {},
    botLoginAltaVideo(_state) {},
    botLogoutAltaVideo(_state) {},
    checkThumbnailPresence(
      _state,
      _action: PayloadAction<{
        visitId: string;
        visitorId: string;
        type: 'SIGNIN' | 'SIGNOUT';
        time: number;
        deviceId: string;
      }>,
    ) {},

    setIntegrationData(
      state,
      action: PayloadAction<{
        integrationData: IntegrationDataType[];
        isInstalled: boolean;
      }>,
    ) {
      const { integrationData, isInstalled } = action.payload;
      return {
        ...state,
        altaVideoIntegrationData: integrationData,
        isAltaVideoInstalled: isInstalled,
        ...(!isInstalled
          ? {
              videoDevices: [],
              devicesByWorkflow: {},
              isLoggedIn: false,
            }
          : {}),
      };
    },
    setDevicesByWorkflow(state, action: PayloadAction<DevicesByWorkflowType>) {
      const locationId = action.payload.location.id;
      return {
        ...state,
        devicesByWorkflow: {
          ...state.devicesByWorkflow,
          [locationId]: action.payload,
        },
      };
    },

    setVideoDevices(state, action: PayloadAction<VideoDeviceType[]>) {
      return { ...state, videoDevices: action.payload };
    },

    setLoginStatus(state, action: PayloadAction<boolean>) {
      return { ...state, isLoggedIn: action.payload };
    },
    setThumbnailLoading(
      state,
      action: PayloadAction<{
        visitId: string;
        visitorId: string;
        type: 'SIGNIN' | 'SIGNOUT';
        delta: number;
      }>,
    ) {
      const { visitId, visitorId, type, delta } = action.payload;
      const current = state.thumbnailLoading[visitId]?.[visitorId]?.[type] ?? 0;
      return {
        ...state,
        thumbnailLoading: {
          ...state.thumbnailLoading,
          [visitId]: {
            ...(state.thumbnailLoading[visitId] || {}),
            [visitorId]: {
              ...(state.thumbnailLoading[visitId]?.[visitorId] || {}),
              [type]: Math.max(0, current + delta),
            },
          },
        },
      };
    },
    setThumbnailPresence(
      state,
      action: PayloadAction<{
        visitId: string;
        visitorId: string;
        type: 'SIGNIN' | 'SIGNOUT';
        has: boolean;
      }>,
    ) {
      const { visitId, visitorId, type, has } = action.payload;
      const existing = state.thumbnailPresence[visitId]?.[visitorId]?.[type];

      if (has === true || existing !== true) {
        return {
          ...state,
          thumbnailPresence: {
            ...state.thumbnailPresence,
            [visitId]: {
              ...(state.thumbnailPresence[visitId] || {}),
              [visitorId]: {
                ...(state.thumbnailPresence[visitId]?.[visitorId] || {}),
                [type]: has,
              },
            },
          },
        };
      }
      return state;
    },
    resetAltaVideoForOrg() {
      return initialState;
    },

    clearDevicesByWorkflow(state) {
      return { ...state, devicesByWorkflow: {} };
    },

    resetThumbnailPresense(state) {
      return { ...state, thumbnailPresence: {}, thumbnailLoading: {} };
    },
  },
});

export const {
  fetchAltaVideoIntegrationData,
  fetchDevicesByWorkflow,
  fetchVideoDevices,
  botLoginAltaVideo,
  botLogoutAltaVideo,
  checkThumbnailPresence,
  setIntegrationData,
  setDevicesByWorkflow,
  setVideoDevices,
  setLoginStatus,
  setThumbnailLoading,
  setThumbnailPresence,
  resetAltaVideoForOrg,
  clearDevicesByWorkflow,
  resetThumbnailPresense,
} = altaVideoSlice.actions;

export default altaVideoSlice.reducer;
